import * as R from 'ramda';
import * as P from 'plow-js';
import { createReducer } from 'redux-act';
// feature analytics
import * as A from './actions';
import { createPageState, appendReportList } from './helpers';
import {
  PAGE_LANE_RPM,
  PAGE_WEEKLY_RPM,
  PAGE_FLEET_ACTIVITY,
} from './constants';
//////////////////////////////////////////////////

const createInitialState = () => ({
  compact: false,
  [PAGE_LANE_RPM]: createPageState(PAGE_LANE_RPM),
  [PAGE_WEEKLY_RPM]: createPageState(PAGE_WEEKLY_RPM),
  [PAGE_FLEET_ACTIVITY]: createPageState(PAGE_FLEET_ACTIVITY),
});

export const initialState = createInitialState();

const setInitialState = () => createInitialState();

const toggleCompact = (state) => P.$toggle('compact', state);

const setFilters = (state, payload) => {
  const { page, ...data } = R.or(payload, {});

  if (R.or(R.isNil(page), R.isEmpty(page))) return state;

  const current = R.pathOr({}, [page, 'filters'], state);

  return P.$set(`${page}.filters`, { ...current, ...data }, state);
};

const setPageOptions = (state, payload) => {
  const { key, page, items, loadedFor } = R.or(payload, {});

  if (R.or(R.isNil(key), R.isNil(page))) return state;

  return P.$set(`${page}.options.${key}`, { items, loadedFor }, state);
};

const setExpanded = (state, { page, key }) => R.assocPath([page, 'expanded'], key, state);

const getAnalyticsWeeklyRpmByRegionReportLoading = (state, payload) => {
  const path = [PAGE_WEEKLY_RPM, 'reports', payload.key];
  const current = R.pathOr({}, path, state);

  const next = {
    ...current,
    error: null,
    loading: true,
    phase: 'loading',
    append: payload.append,
    request: payload.request,
    requestId: payload.requestId,
    details: payload.append ? current.details : {},
  };

  const nextState = payload.append ? state : R.assocPath([PAGE_WEEKLY_RPM, 'expanded'], null, state);

  return R.assocPath(path, next, nextState);
};

const getAnalyticsWeeklyRpmByRegionReportSuccess = (state, payload) => {
  const path = [PAGE_WEEKLY_RPM, 'reports', payload.key];
  const current = R.pathOr({}, path, state);

  if (current.requestId !== payload.requestId) return state;

  const next = {
    ...current,
    error: null,
    loading: false,
    phase: 'success',
    revision: (current.revision || 0) + 1,
    applied: payload.append ? current.applied : payload.request.payload.applied,
    report: payload.append ? appendReportList(current.report, payload.data, 'rows') : payload.data,
  };

  return R.assocPath(path, next, state);
};

const getAnalyticsWeeklyRpmByRegionReportFailure = (state, payload) => {
  const path = [PAGE_WEEKLY_RPM, 'reports', payload.key];
  const current = R.pathOr({}, path, state);

  if (current.requestId !== payload.requestId) return state;

  const next = {
    ...current,
    loading: false,
    phase: 'error',
    error: payload.error,
  };

  return R.assocPath(path, next, state);
};

const getAnalyticsWeeklyRpmByRegionReportOrdersLoading = (state, payload) => {
  const path = [PAGE_WEEKLY_RPM, 'reports', payload.key, 'details', payload.rowKey];
  const current = R.pathOr({}, path, state);

  const next = {
    ...current,
    error: null,
    loading: true,
    phase: 'loading',
    append: payload.append,
    request: payload.request,
    requestId: payload.requestId,
    orders: current.orders || [],
    totalCount: current.totalCount || 0,
    laneTotal: current.laneTotal || null,
  };

  return R.assocPath(path, next, state);
};

const getAnalyticsWeeklyRpmByRegionReportOrdersSuccess = (state, payload) => {
  const path = [PAGE_WEEKLY_RPM, 'reports', payload.key, 'details', payload.rowKey];
  const current = R.pathOr({}, path, state);

  if (current.requestId !== payload.requestId) return state;

  const next = {
    ...current,
    error: null,
    loading: false,
    phase: 'success',
    ...payload.data,
    laneTotal: payload.data.laneTotal || current.laneTotal,
    orders: [...(payload.append ? current.orders || [] : []), ...(payload.data.orders || [])],
  };

  return R.assocPath(path, next, state);
};

const getAnalyticsWeeklyRpmByRegionReportOrdersFailure = (state, payload) => {
  const path = [PAGE_WEEKLY_RPM, 'reports', payload.key, 'details', payload.rowKey];
  const current = R.pathOr({}, path, state);

  if (current.requestId !== payload.requestId) return state;

  const next = {
    ...current,
    loading: false,
    phase: 'error',
    error: payload.error,
  };

  const collapse = !payload.append && state[PAGE_WEEKLY_RPM].expanded === payload.rowKey;
  const nextState = collapse ? R.assocPath([PAGE_WEEKLY_RPM, 'expanded'], null, state) : state;

  return R.assocPath(path, next, nextState);
};

const getAnalyticsFleetActivityReportLoading = (state, payload) => {
  const path = [PAGE_FLEET_ACTIVITY, 'reports', payload.key];
  const current = R.pathOr({}, path, state);

  const next = {
    ...current,
    error: null,
    loading: true,
    phase: 'loading',
    append: payload.append,
    request: payload.request,
    requestId: payload.requestId,
    details: payload.append ? current.details : {},
  };

  const nextState = payload.append ? state : R.assocPath([PAGE_FLEET_ACTIVITY, 'expanded'], null, state);

  return R.assocPath(path, next, nextState);
};

const getAnalyticsFleetActivityReportSuccess = (state, payload) => {
  const path = [PAGE_FLEET_ACTIVITY, 'reports', payload.key];
  const current = R.pathOr({}, path, state);

  if (current.requestId !== payload.requestId) return state;

  const next = {
    ...current,
    error: null,
    loading: false,
    phase: 'success',
    revision: (current.revision || 0) + 1,
    applied: payload.append ? current.applied : payload.request.payload.applied,
    report: payload.append ? appendReportList(current.report, payload.data, 'drivers') : payload.data,
  };

  return R.assocPath(path, next, state);
};

const getAnalyticsFleetActivityReportFailure = (state, payload) => {
  const path = [PAGE_FLEET_ACTIVITY, 'reports', payload.key];
  const current = R.pathOr({}, path, state);

  if (current.requestId !== payload.requestId) return state;

  const next = {
    ...current,
    loading: false,
    phase: 'error',
    error: payload.error,
  };

  return R.assocPath(path, next, state);
};

const getAnalyticsFleetActivityReportTelsLoading = (state, payload) => {
  const path = [PAGE_FLEET_ACTIVITY, 'reports', payload.key, 'details', payload.rowKey];
  const current = R.pathOr({}, path, state);

  const next = {
    ...current,
    error: null,
    loading: true,
    phase: 'loading',
    append: payload.append,
    request: payload.request,
    tels: current.tels || [],
    requestId: payload.requestId,
    totalCount: current.totalCount || 0,
    laneTotal: current.laneTotal || null,
  };

  return R.assocPath(path, next, state);
};

const getAnalyticsFleetActivityReportTelsSuccess = (state, payload) => {
  const path = [PAGE_FLEET_ACTIVITY, 'reports', payload.key, 'details', payload.rowKey];
  const current = R.pathOr({}, path, state);

  if (current.requestId !== payload.requestId) return state;

  const next = {
    ...current,
    error: null,
    loading: false,
    phase: 'success',
    ...payload.data,
    laneTotal: payload.data.laneTotal || current.laneTotal,
    tels: [...(payload.append ? current.tels || [] : []), ...(payload.data.tels || [])],
  };

  return R.assocPath(path, next, state);
};

const getAnalyticsFleetActivityReportTelsFailure = (state, payload) => {
  const path = [PAGE_FLEET_ACTIVITY, 'reports', payload.key, 'details', payload.rowKey];
  const current = R.pathOr({}, path, state);

  if (current.requestId !== payload.requestId) return state;

  const next = {
    ...current,
    loading: false,
    phase: 'error',
    error: payload.error,
  };

  const collapse = !payload.append && state[PAGE_FLEET_ACTIVITY].expanded === payload.rowKey;
  const nextState = collapse ? R.assocPath([PAGE_FLEET_ACTIVITY, 'expanded'], null, state) : state;

  return R.assocPath(path, next, nextState);
};

const getAnalyticsLaneRpmReportLoading = (state, payload) => {
  const path = [PAGE_LANE_RPM, 'reports', payload.key];
  const current = R.pathOr({}, path, state);

  const next = {
    ...current,
    error: null,
    loading: true,
    phase: 'loading',
    append: payload.append,
    request: payload.request,
    requestId: payload.requestId,
    details: payload.append ? current.details : {},
  };

  const nextState = payload.append ? state : R.assocPath([PAGE_LANE_RPM, 'expanded'], null, state);

  return R.assocPath(path, next, nextState);
};

const getAnalyticsLaneRpmReportSuccess = (state, payload) => {
  const path = [PAGE_LANE_RPM, 'reports', payload.key];
  const current = R.pathOr({}, path, state);

  if (current.requestId !== payload.requestId) return state;

  const next = {
    ...current,
    error: null,
    loading: false,
    phase: 'success',
    revision: (current.revision || 0) + 1,
    applied: payload.append ? current.applied : payload.request.payload.applied,
    report: payload.append ? appendReportList(current.report, payload.data, 'lanes') : payload.data,
  };

  return R.assocPath(path, next, state);
};

const getAnalyticsLaneRpmReportFailure = (state, payload) => {
  const path = [PAGE_LANE_RPM, 'reports', payload.key];
  const current = R.pathOr({}, path, state);

  if (current.requestId !== payload.requestId) return state;

  const next = {
    ...current,
    loading: false,
    phase: 'error',
    error: payload.error,
  };

  return R.assocPath(path, next, state);
};

const getAnalyticsLaneRpmReportLanesLoading = (state, payload) => {
  const path = [PAGE_LANE_RPM, 'reports', payload.key];
  const current = R.pathOr({}, path, state);

  const next = {
    ...current,
    error: null,
    loading: true,
    phase: 'loading',
    append: payload.append,
    request: payload.request,
    requestId: payload.requestId,
    details: payload.append ? current.details : {},
  };

  const nextState = payload.append ? state : R.assocPath([PAGE_LANE_RPM, 'expanded'], null, state);

  return R.assocPath(path, next, nextState);
};

const getAnalyticsLaneRpmReportLanesSuccess = (state, payload) => {
  const path = [PAGE_LANE_RPM, 'reports', payload.key];
  const current = R.pathOr({}, path, state);

  if (current.requestId !== payload.requestId) return state;

  const next = {
    ...current,
    error: null,
    loading: false,
    phase: 'success',
    revision: (current.revision || 0) + 1,
    applied: payload.append ? current.applied : payload.request.payload.applied,
    report: payload.append ? appendReportList(current.report, payload.data, 'lanes') : payload.data,
  };

  return R.assocPath(path, next, state);
};

const getAnalyticsLaneRpmReportLanesFailure = (state, payload) => {
  const path = [PAGE_LANE_RPM, 'reports', payload.key];
  const current = R.pathOr({}, path, state);

  if (current.requestId !== payload.requestId) return state;

  const next = {
    ...current,
    loading: false,
    phase: 'error',
    error: payload.error,
  };

  return R.assocPath(path, next, state);
};

const getAnalyticsLaneRpmReportLaneOrdersLoading = (state, payload) => {
  const path = [PAGE_LANE_RPM, 'reports', payload.key, 'details', payload.rowKey];
  const current = R.pathOr({}, path, state);

  const next = {
    ...current,
    error: null,
    loading: true,
    phase: 'loading',
    append: payload.append,
    request: payload.request,
    requestId: payload.requestId,
    orders: current.orders || [],
    totalCount: current.totalCount || 0,
    laneTotal: current.laneTotal || null,
  };

  return R.assocPath(path, next, state);
};

const getAnalyticsLaneRpmReportLaneOrdersSuccess = (state, payload) => {
  const path = [PAGE_LANE_RPM, 'reports', payload.key, 'details', payload.rowKey];
  const current = R.pathOr({}, path, state);

  if (current.requestId !== payload.requestId) return state;

  const next = {
    ...current,
    error: null,
    loading: false,
    phase: 'success',
    ...payload.data,
    laneTotal: payload.data.laneTotal || current.laneTotal,
    orders: [...(payload.append ? current.orders || [] : []), ...(payload.data.orders || [])],
  };

  return R.assocPath(path, next, state);
};

const getAnalyticsLaneRpmReportLaneOrdersFailure = (state, payload) => {
  const path = [PAGE_LANE_RPM, 'reports', payload.key, 'details', payload.rowKey];
  const current = R.pathOr({}, path, state);

  if (current.requestId !== payload.requestId) return state;

  const next = {
    ...current,
    loading: false,
    phase: 'error',
    error: payload.error,
  };

  const collapse = !payload.append && state[PAGE_LANE_RPM].expanded === payload.rowKey;
  const nextState = collapse ? R.assocPath([PAGE_LANE_RPM, 'expanded'], null, state) : state;

  return R.assocPath(path, next, nextState);
};

const getAnalyticsLaneRpmReportLaneCustomersLoading = (state, payload) => {
  const path = [PAGE_LANE_RPM, 'reports', payload.key, 'details', payload.rowKey];
  const current = R.pathOr({}, path, state);

  const next = {
    ...current,
    error: null,
    loading: true,
    phase: 'loading',
    append: payload.append,
    request: payload.request,
    requestId: payload.requestId,
    customers: current.customers || [],
    totalCount: current.totalCount || 0,
    laneTotal: current.laneTotal || null,
  };

  return R.assocPath(path, next, state);
};

const getAnalyticsLaneRpmReportLaneCustomersSuccess = (state, payload) => {
  const path = [PAGE_LANE_RPM, 'reports', payload.key, 'details', payload.rowKey];
  const current = R.pathOr({}, path, state);

  if (current.requestId !== payload.requestId) return state;

  const next = {
    ...current,
    error: null,
    loading: false,
    phase: 'success',
    ...payload.data,
    laneTotal: payload.data.laneTotal || current.laneTotal,
    customers: [...(payload.append ? current.customers || [] : []), ...(payload.data.customers || [])],
  };

  return R.assocPath(path, next, state);
};

const getAnalyticsLaneRpmReportLaneCustomersFailure = (state, payload) => {
  const path = [PAGE_LANE_RPM, 'reports', payload.key, 'details', payload.rowKey];
  const current = R.pathOr({}, path, state);

  if (current.requestId !== payload.requestId) return state;

  const next = {
    ...current,
    loading: false,
    phase: 'error',
    error: payload.error,
  };

  const collapse = !payload.append && state[PAGE_LANE_RPM].expanded === payload.rowKey;
  const nextState = collapse ? R.assocPath([PAGE_LANE_RPM, 'expanded'], null, state) : state;

  return R.assocPath(path, next, nextState);
};

const getAnalyticsRegionsLoading = (state, payload) => {
  const path = [payload.page, 'options', 'regions'];
  const current = R.pathOr({}, path, state);

  const next = {
    ...current,
    error: null,
    loading: true,
    phase: 'loading',
    request: payload.request,
    requestId: payload.requestId,
  };

  return R.assocPath(path, next, state);
};

const getAnalyticsRegionsSuccess = (state, payload) => {
  const path = [payload.page, 'options', 'regions'];
  const current = R.pathOr({}, path, state);

  if (current.requestId !== payload.requestId) return state;

  const next = {
    ...current,
    error: null,
    loading: false,
    phase: 'success',
    items: payload.data,
    loadedFor: payload.branch,
  };

  return R.assocPath(path, next, state);
};

const getAnalyticsRegionsFailure = (state, payload) => {
  const path = [payload.page, 'options', 'regions'];
  const current = R.pathOr({}, path, state);

  if (current.requestId !== payload.requestId) return state;

  const next = {
    ...current,
    loading: false,
    phase: 'error',
    error: payload.error,
  };

  return R.assocPath(path, next, state);
};

const getListFleetDriversDownLoading = (state, payload) => {
  const path = [payload.page, 'options', 'drivers'];
  const current = R.pathOr({}, path, state);

  const next = {
    ...current,
    error: null,
    loading: true,
    phase: 'loading',
    request: payload.request,
    requestId: payload.requestId,
  };

  return R.assocPath(path, next, state);
};

const getListFleetDriversDownSuccess = (state, payload) => {
  const path = [payload.page, 'options', 'drivers'];
  const current = R.pathOr({}, path, state);

  if (current.requestId !== payload.requestId) return state;

  const next = {
    ...current,
    error: null,
    loading: false,
    phase: 'success',
    items: payload.data,
    loadedFor: payload.branch,
  };

  return R.assocPath(path, next, state);
};

const getListFleetDriversDownFailure = (state, payload) => {
  const path = [payload.page, 'options', 'drivers'];
  const current = R.pathOr({}, path, state);

  if (current.requestId !== payload.requestId) return state;

  const next = {
    ...current,
    loading: false,
    phase: 'error',
    error: payload.error,
  };

  return R.assocPath(path, next, state);
};

const getAnalyticsSyncStatusLoading = (state, payload) => {
  const path = ['syncStatus', payload.branch || 'default'];
  const current = R.pathOr({}, path, state);

  const next = {
    ...current,
    error: null,
    loading: true,
    phase: 'loading',
    request: payload.request,
    requestId: payload.requestId,
  };

  return R.assocPath(path, next, state);
};

const getAnalyticsSyncStatusSuccess = (state, payload) => {
  const path = ['syncStatus', payload.branch || 'default'];
  const current = R.pathOr({}, path, state);

  if (current.requestId !== payload.requestId) return state;

  const next = {
    ...current,
    error: null,
    loading: false,
    phase: 'success',
    data: payload.data,
    receivedAt: payload.receivedAt,
  };

  return R.assocPath(path, next, state);
};

const getAnalyticsSyncStatusFailure = (state, payload) => {
  const path = ['syncStatus', payload.branch || 'default'];
  const current = R.pathOr({}, path, state);

  if (current.requestId !== payload.requestId) return state;

  const next = {
    ...current,
    loading: false,
    phase: 'error',
    error: payload.error,
  };

  return R.assocPath(path, next, state);
};

const setWeeklyRpmLoading = (state, { key, loading, requestId }) => {
  const path = [PAGE_WEEKLY_RPM, 'reports', key];
  const current = R.path(path, state);

  if (!current || current.requestId !== requestId) return state;

  return R.assocPath([...path, 'loading'], loading, state);
};

const setFleetActivityLoading = (state, { key, loading, requestId }) => {
  const path = [PAGE_FLEET_ACTIVITY, 'reports', key];
  const current = R.path(path, state);

  if (!current || current.requestId !== requestId) return state;

  return R.assocPath([...path, 'loading'], loading, state);
};

const setLaneRpmLoading = (state, { key, loading, requestId }) => {
  const path = [PAGE_LANE_RPM, 'reports', key];
  const current = R.path(path, state);

  if (!current || current.requestId !== requestId) return state;

  return R.assocPath([...path, 'loading'], loading, state);
};

export default createReducer({
  [A.setFilters]: setFilters,
  [A.setExpanded]: setExpanded,
  [A.toggleCompact]: toggleCompact,
  [A.setPageOptions]: setPageOptions,
  [A.setInitialState]: setInitialState,
  [A.setLaneRpmLoading]: setLaneRpmLoading,
  [A.setWeeklyRpmLoading]: setWeeklyRpmLoading,
  [A.setFleetActivityLoading]: setFleetActivityLoading,
  [A.getAnalyticsRegionsLoading]: getAnalyticsRegionsLoading,
  [A.getAnalyticsRegionsSuccess]: getAnalyticsRegionsSuccess,
  [A.getAnalyticsRegionsFailure]: getAnalyticsRegionsFailure,
  [A.getAnalyticsSyncStatusLoading]: getAnalyticsSyncStatusLoading,
  [A.getAnalyticsSyncStatusSuccess]: getAnalyticsSyncStatusSuccess,
  [A.getAnalyticsSyncStatusFailure]: getAnalyticsSyncStatusFailure,
  [A.getListFleetDriversDownLoading]: getListFleetDriversDownLoading,
  [A.getListFleetDriversDownSuccess]: getListFleetDriversDownSuccess,
  [A.getListFleetDriversDownFailure]: getListFleetDriversDownFailure,
  [A.getAnalyticsLaneRpmReportLoading]: getAnalyticsLaneRpmReportLoading,
  [A.getAnalyticsLaneRpmReportSuccess]: getAnalyticsLaneRpmReportSuccess,
  [A.getAnalyticsLaneRpmReportFailure]: getAnalyticsLaneRpmReportFailure,
  [A.getAnalyticsLaneRpmReportLanesLoading]: getAnalyticsLaneRpmReportLanesLoading,
  [A.getAnalyticsLaneRpmReportLanesSuccess]: getAnalyticsLaneRpmReportLanesSuccess,
  [A.getAnalyticsLaneRpmReportLanesFailure]: getAnalyticsLaneRpmReportLanesFailure,
  [A.getAnalyticsFleetActivityReportLoading]: getAnalyticsFleetActivityReportLoading,
  [A.getAnalyticsFleetActivityReportSuccess]: getAnalyticsFleetActivityReportSuccess,
  [A.getAnalyticsFleetActivityReportFailure]: getAnalyticsFleetActivityReportFailure,
  [A.getAnalyticsWeeklyRpmByRegionReportLoading]: getAnalyticsWeeklyRpmByRegionReportLoading,
  [A.getAnalyticsWeeklyRpmByRegionReportSuccess]: getAnalyticsWeeklyRpmByRegionReportSuccess,
  [A.getAnalyticsWeeklyRpmByRegionReportFailure]: getAnalyticsWeeklyRpmByRegionReportFailure,
  [A.getAnalyticsFleetActivityReportTelsLoading]: getAnalyticsFleetActivityReportTelsLoading,
  [A.getAnalyticsFleetActivityReportTelsSuccess]: getAnalyticsFleetActivityReportTelsSuccess,
  [A.getAnalyticsFleetActivityReportTelsFailure]: getAnalyticsFleetActivityReportTelsFailure,
  [A.getAnalyticsLaneRpmReportLaneOrdersLoading]: getAnalyticsLaneRpmReportLaneOrdersLoading,
  [A.getAnalyticsLaneRpmReportLaneOrdersSuccess]: getAnalyticsLaneRpmReportLaneOrdersSuccess,
  [A.getAnalyticsLaneRpmReportLaneOrdersFailure]: getAnalyticsLaneRpmReportLaneOrdersFailure,
  [A.getAnalyticsLaneRpmReportLaneCustomersLoading]: getAnalyticsLaneRpmReportLaneCustomersLoading,
  [A.getAnalyticsLaneRpmReportLaneCustomersSuccess]: getAnalyticsLaneRpmReportLaneCustomersSuccess,
  [A.getAnalyticsLaneRpmReportLaneCustomersFailure]: getAnalyticsLaneRpmReportLaneCustomersFailure,
  [A.getAnalyticsWeeklyRpmByRegionReportOrdersLoading]: getAnalyticsWeeklyRpmByRegionReportOrdersLoading,
  [A.getAnalyticsWeeklyRpmByRegionReportOrdersSuccess]: getAnalyticsWeeklyRpmByRegionReportOrdersSuccess,
  [A.getAnalyticsWeeklyRpmByRegionReportOrdersFailure]: getAnalyticsWeeklyRpmByRegionReportOrdersFailure,
}, initialState);
